import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CustomerSearchSelect } from '@/components/ui/customer-search-select';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useModules } from '@/contexts/ModulesContext';
import { useNavigate } from 'react-router-dom';
import { Loader2, Save, Smile, FileDown, ClipboardList } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Zahnstatus → Farbe + deutsches Label (FDI).
const STATUS = {
  healthy:    { label: 'Gesund',          color: '#e5e7eb', text: '#374151' },
  caries:     { label: 'Karies',          color: '#ef4444', text: '#ffffff' },
  filling:    { label: 'Füllung',         color: '#3b82f6', text: '#ffffff' },
  crown:      { label: 'Krone',           color: '#f59e0b', text: '#1f2937' },
  missing:    { label: 'Fehlt',           color: '#1f2937', text: '#ffffff' },
  root_canal: { label: 'Wurzelbehandlung',color: '#8b5cf6', text: '#ffffff' },
  implant:    { label: 'Implantat',       color: '#14b8a6', text: '#ffffff' },
  bridge:     { label: 'Brücke',          color: '#ec4899', text: '#ffffff' },
};
const STATUS_KEYS = ['healthy', 'caries', 'filling', 'crown', 'missing', 'root_canal', 'implant', 'bridge'];

const DentalChartPage = () => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [teeth, setTeeth] = useState({});          // { fdi: {status, note} }
  const [fdiOrder, setFdiOrder] = useState([]);
  const [selectedTooth, setSelectedTooth] = useState(null);
  const navigate = useNavigate();
  const { isModuleEnabled } = useModules();
  const [planOpen, setPlanOpen] = useState(false);
  const [planTitle, setPlanTitle] = useState('Behandlungsplan');
  const [planTeeth, setPlanTeeth] = useState([]);
  const [creatingPlan, setCreatingPlan] = useState(false);

  const openPlan = () => {
    const findings = Object.entries(teeth).filter(([, v]) => v.status && v.status !== 'healthy');
    const pre = findings.filter(([, v]) => ['caries', 'root_canal', 'missing'].includes(v.status)).map(([f]) => f);
    setPlanTeeth(pre.length ? pre : findings.map(([f]) => f));
    setPlanTitle('Behandlungsplan');
    setPlanOpen(true);
  };
  const togglePlanTooth = (fdi) => setPlanTeeth((prev) => (prev.includes(fdi) ? prev.filter((x) => x !== fdi) : [...prev, fdi]));
  const createPlan = async () => {
    if (planTeeth.length === 0) { toast.error('Bitte mindestens einen Zahn wählen'); return; }
    setCreatingPlan(true);
    try {
      await axios.post(`${API}/dental/billing/plans/from-chart`, { customer_id: customerId, teeth: planTeeth, title: planTitle });
      toast.success('Behandlungsplan aus Zahnschema erstellt');
      setPlanOpen(false);
      navigate('/dental-plans');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Konnte nicht erstellt werden'); }
    finally { setCreatingPlan(false); }
  };
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [visitNote, setVisitNote] = useState('');
  const [history, setHistory] = useState([]);
  const [pdfLang, setPdfLang] = useState('de');

  useEffect(() => {
    axios.get(`${API}/customers`).then((r) => {
      setCustomers(Array.isArray(r.data) ? r.data : (r.data?.customers || []));
    }).catch(() => setCustomers([]));
  }, []);

  const loadChart = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        axios.get(`${API}/dental/chart/${id}`),
        axios.get(`${API}/dental/chart/${id}/history`),
      ]);
      setTeeth(r.data?.teeth || {});
      setFdiOrder(r.data?.fdi_order || []);
      setHistory(h.data || []);
      setSelectedTooth(null);
      setVisitNote('');
    } catch {
      toast.error('Zahnschema konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (customerId) loadChart(customerId); }, [customerId, loadChart]);

  const toothOf = (fdi) => teeth[fdi] || { status: 'healthy', note: null };

  const setToothStatus = (fdi, status) => {
    setTeeth((prev) => ({ ...prev, [fdi]: { status, note: prev[fdi]?.note || null } }));
  };
  const setToothNote = (fdi, note) => {
    setTeeth((prev) => ({ ...prev, [fdi]: { status: prev[fdi]?.status || 'healthy', note } }));
  };

  const save = async () => {
    if (!customerId) return;
    setSaving(true);
    try {
      const payload = { teeth: Object.fromEntries(Object.entries(teeth).map(([k, v]) => [k, { status: v.status, note: v.note || null }])), visit_note: visitNote || null };
      const r = await axios.put(`${API}/dental/chart/${customerId}`, payload);
      setTeeth(r.data?.teeth || {});
      setVisitNote('');
      try { const h = await axios.get(`${API}/dental/chart/${customerId}/history`); setHistory(h.data || []); } catch { /* ignore */ }
      toast.success('Zahnschema gespeichert');
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (historyId) => {
    if (!customerId) return;
    try {
      const r = await axios.get(`${API}/dental/chart/${customerId}/pdf`, {
        params: { ...(historyId ? { history_id: historyId } : {}), lang: pdfLang },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const base = pdfLang === 'fr' ? 'Bilan_dentaire' : pdfLang === 'it' ? 'Referto_dentale' : 'Zahnbefund';
      a.download = `${base}${historyId ? '_' + historyId.slice(0, 8) : ''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF-Export fehlgeschlagen');
    }
  };

  const upper = fdiOrder.slice(0, 16);
  const lower = fdiOrder.slice(16, 32);

  const Tooth = ({ fdi }) => {
    const st = toothOf(fdi);
    const cfg = STATUS[st.status] || STATUS.healthy;
    const isSel = selectedTooth === fdi;
    return (
      <button
        type="button"
        data-testid={`dental-tooth-${fdi}`}
        onClick={() => setSelectedTooth(fdi)}
        title={`${fdi} · ${cfg.label}${st.note ? ' · ' + st.note : ''}`}
        className={`relative flex flex-col items-center justify-center rounded-md border transition-all w-9 h-12 text-[11px] font-semibold ${isSel ? 'ring-2 ring-teal-500 border-teal-500 scale-105' : 'border-gray-300 hover:border-gray-400'}`}
        style={{ background: cfg.color, color: cfg.text }}
      >
        <span>{fdi}</span>
        {st.note ? <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-teal-500 border border-white" /> : null}
      </button>
    );
  };

  const selCfg = selectedTooth ? (STATUS[toothOf(selectedTooth).status] || STATUS.healthy) : null;

  return (
    <div className="space-y-6" data-testid="dental-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
          <Smile className="text-teal-600" size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Zahnschema (FDI)</h1>
          <p className="text-sm text-gray-500">Zahnstatus je Patient erfassen und bei jedem Besuch aktualisieren.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Patient</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md" data-testid="dental-customer-select">
            <CustomerSearchSelect customers={customers} value={customerId} onValueChange={setCustomerId} placeholder="Patient suchen..." />
          </div>
        </CardContent>
      </Card>

      {!customerId ? (
        <Card><CardContent className="py-12 text-center text-gray-400">Bitte zuerst einen Patienten auswählen.</CardContent></Card>
      ) : loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-500" /></div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base">Zahnbogen</CardTitle>
              <div className="flex gap-2">
                <select
                  value={pdfLang}
                  onChange={(e) => setPdfLang(e.target.value)}
                  data-testid="dental-pdf-lang-select"
                  className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  title="Sprache des Befund-PDF"
                >
                  <option value="de">Deutsch</option>
                  <option value="fr">Français</option>
                  <option value="it">Italiano</option>
                </select>
                <Button variant="outline" onClick={() => downloadPdf()} data-testid="dental-pdf-btn">
                  <FileDown className="h-4 w-4 mr-2" /> Befund-PDF
                </Button>
                {isModuleEnabled('dental_billing') && (
                  <Button variant="outline" onClick={openPlan} data-testid="dental-create-plan-btn">
                    <ClipboardList className="h-4 w-4 mr-2" /> Behandlungsplan
                  </Button>
                )}
                <Button onClick={save} disabled={saving} data-testid="dental-save-btn" className="bg-teal-600 hover:bg-teal-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Speichern
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <div className="text-xs text-gray-400 mb-1 ml-1">Oberkiefer</div>
                <div className="flex flex-wrap gap-1.5">
                  {upper.map((fdi, i) => (
                    <React.Fragment key={fdi}>
                      {i === 8 ? <div className="w-px bg-gray-300 mx-1 self-stretch" /> : null}
                      <Tooth fdi={fdi} />
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1 ml-1">Unterkiefer</div>
                <div className="flex flex-wrap gap-1.5">
                  {lower.map((fdi, i) => (
                    <React.Fragment key={fdi}>
                      {i === 8 ? <div className="w-px bg-gray-300 mx-1 self-stretch" /> : null}
                      <Tooth fdi={fdi} />
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {STATUS_KEYS.map((k) => (
                  <span key={k} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span className="w-3 h-3 rounded-sm border border-gray-300" style={{ background: STATUS[k].color }} />
                    {STATUS[k].label}
                  </span>
                ))}
              </div>

              <div className="pt-2 border-t">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Besuchsnotiz (optional)</label>
                <Textarea
                  data-testid="dental-visit-note"
                  rows={2}
                  value={visitNote}
                  onChange={(e) => setVisitNote(e.target.value)}
                  placeholder="z. B. Kontrolle, PZR durchgeführt – wird im Verlauf gespeichert"
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">{selectedTooth ? `Zahn ${selectedTooth}` : 'Zahn auswählen'}</CardTitle></CardHeader>
            <CardContent>
              {!selectedTooth ? (
                <p className="text-sm text-gray-400">Klicke einen Zahn, um Status und Notiz zu setzen.</p>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded border border-gray-300" style={{ background: selCfg?.color }} />
                    <span className="text-sm font-medium">{selCfg?.label}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {STATUS_KEYS.map((k) => (
                      <button
                        key={k}
                        type="button"
                        data-testid={`dental-status-${k}`}
                        onClick={() => setToothStatus(selectedTooth, k)}
                        className={`text-[11px] leading-tight rounded-md border px-2 py-2 text-left transition-all ${toothOf(selectedTooth).status === k ? 'ring-2 ring-teal-500 border-teal-500' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle" style={{ background: STATUS[k].color }} />
                        {STATUS[k].label}
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Notiz</label>
                    <Textarea
                      data-testid="dental-note-input"
                      rows={3}
                      value={toothOf(selectedTooth).note || ''}
                      onChange={(e) => setToothNote(selectedTooth, e.target.value)}
                      placeholder="z. B. distal, Kontrolle in 6 Monaten"
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="dental-history-card">
            <CardHeader className="pb-2"><CardTitle className="text-base">Verlauf</CardTitle></CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-gray-400">Noch keine gespeicherten Besuche.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {history.map((h) => (
                    <div key={h.id} className="flex items-center justify-between gap-2 rounded-md border border-gray-200 p-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{(h.created_at || '').slice(0, 10)}</div>
                        <div className="text-xs text-gray-500 truncate">{h.finding_count} Befund(e){h.visit_note ? ' · ' + h.visit_note : ''}</div>
                      </div>
                      <Button variant="ghost" size="sm" data-testid={`dental-history-pdf-${h.id}`} onClick={() => downloadPdf(h.id)}><FileDown className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        </div>
      )}

      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent data-testid="dental-plan-dialog">
          <DialogHeader><DialogTitle>Behandlungsplan aus Zahnschema</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Titel</label>
              <Input value={planTitle} onChange={(e) => setPlanTitle(e.target.value)} data-testid="dental-plan-title" />
            </div>
            <div className="text-sm font-medium text-slate-700">Zähne mit Befund</div>
            <div className="max-h-64 overflow-y-auto space-y-1 border rounded-lg p-2">
              {Object.entries(teeth).filter(([, v]) => v.status && v.status !== 'healthy').length === 0 && (
                <div className="text-sm text-slate-400 px-1 py-2">Keine Befunde im Zahnschema. Bitte zuerst Zähne markieren und speichern.</div>
              )}
              {Object.entries(teeth)
                .filter(([, v]) => v.status && v.status !== 'healthy')
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([fdi, v]) => (
                  <label key={fdi} className="flex items-center gap-2 px-1 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm" data-testid={`dental-plan-tooth-${fdi}`}>
                    <input type="checkbox" checked={planTeeth.includes(fdi)} onChange={() => togglePlanTooth(fdi)} data-testid={`dental-plan-check-${fdi}`} />
                    <span className="font-semibold w-8">{fdi}</span>
                    <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: (STATUS[v.status] || {}).color }} />
                    <span className="text-slate-600">{(STATUS[v.status] || {}).label || v.status}</span>
                    {v.note ? <span className="text-slate-400 truncate"> · {v.note}</span> : null}
                  </label>
                ))}
            </div>
            <p className="text-xs text-slate-400">Die gewählten Zähne werden dem Plan als Befund-Notiz hinzugefügt. Tarifpositionen fügen Sie anschliessend je Sitzung hinzu.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanOpen(false)}>Abbrechen</Button>
            <Button onClick={createPlan} disabled={creatingPlan || planTeeth.length === 0} data-testid="dental-plan-create-btn">Plan erstellen ({planTeeth.length})</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DentalChartPage;
